import { api, type ApiResponse } from './api'
import type { CustomerSuggestion } from './customers'

/**
 * Cliente de cotizaciones.
 *
 * A diferencia del catálogo, aquí la escritura sí tiene una forma única: una
 * cotización es un documento con cabecera e ítems, y separarla en recursos
 * distintos obligaría al cliente a mantener la coherencia entre ellos.
 */

export type QuoteStatus = 'borrador' | 'pendiente' | 'separada' | 'empacada' | 'enviada' | 'cancelada'

export type PriceType = 'retail' | 'wholesale'

export type NoteType = 'general' | 'llamada' | 'whatsapp' | 'visita'

/** De dónde viene un ítem. Lo decide el cotizador, no el total. */
export type ItemOrigin = 'product' | 'set' | 'custom'

export interface QuoteItem {
  id: number
  parent_item_id: number | null
  reference: string | null
  name: string
  price: string
  quantity: string
  is_set: boolean
  is_predefinido: boolean
  origin: ItemOrigin
  product_id: number | null
  set_id: number | null
  line_total: string
  children: QuoteItem[]
}

/** Medio de pago de un anticipo, tal y como lo guarda el backend. */
export type AdvancePaymentMethod =
  | 'efectivo'
  | 'transferencia'
  | 'tarjeta_credito'
  | 'tarjeta_debito'
  | 'cheque'
  | 'pse'
  | 'consignacion'

/**
 * Los medios de pago y cómo se escriben.
 *
 * La lista vive en el frontend y no se pide al backend a propósito: son datos de
 * lectura que hay que tener para pintar el desplegable, y una petición extra por
 * abrir la pantalla es una espera por una lista de siete textos.
 *
 * Las claves tienen que coincidir con `QuoteAdvance::PAYMENT_METHODS` del backend.
 */
export const ADVANCE_PAYMENT_METHODS: Array<{ value: AdvancePaymentMethod; label: string }> = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta_credito', label: 'Tarjeta de crédito' },
  { value: 'tarjeta_debito', label: 'Tarjeta de débito' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'pse', label: 'PSE' },
  { value: 'consignacion', label: 'Consignación' },
]

/** Las dos cifras que se recalculan al tocar un anticipo. */
export interface AdvanceTotals {
  advances_total: string
  balance: string
}

/** Un anticipo que el cliente pagó sobre la cotización. */
export interface QuoteAdvance {
  id: number
  amount: string
  /** Fecha en `d/m/Y`, que es como la devuelve el backend. */
  paid_on: string | null
  payment_method: AdvancePaymentMethod
  /** El medio ya escrito para el usuario, no el código. */
  payment_method_label?: string
  note: string | null
  created_at: string | null
}

/** Lo que se envía al dar de alta un anticipo. */
export interface QuoteAdvanceInput {
  amount: number
  /** Fecha en `aaaa-mm-dd`. El backend la formatea al responder. */
  paid_on: string
  payment_method: AdvancePaymentMethod
  note?: string | null
}

export interface QuoteNote {
  id: number
  note: string
  type: NoteType
  user_id: number | null
  user_name: string | null
  next_follow_up_date: string | null
  created_at: string | null
}

export interface Quote {
  id: number
  quote_number: string | null
  customer_id: number | null
  customer_name: string
  document: string | null
  /**
   * Copia local del contacto del cliente, no una lectura del directorio.
   *
   * Es lo que hace que una cotización ya emitida siga teniendo el correo y el
   * teléfono con los que se emitió, aunque el cliente los cambie después o se borre
   * del directorio.
   */
  customer_email: string | null
  customer_phone: string | null
  /** Ciudad y origen del cliente, también como copia local. */
  customer_city: string | null
  customer_source: string | null
  attention_line: string | null
  user_id: number | null
  user_name: string | null
  quote_date: string
  price_type: PriceType
  status: QuoteStatus
  total_price: string
  is_invoiced: boolean
  invoice_number: string | null
  unread_notes_count: number
  next_follow_up_date: string | null
  last_follow_up_note: string | null
  items: QuoteItem[]
  /**
   * Cuántos productos contiene la cotización. Los sets cuentan por sus
   * componentes, no por el set: un set de tres artículos son tres productos.
   * Viene calculado por el servidor porque los hijos viajan dentro de su padre.
   */
  products_count: number
  notes: QuoteNote[]
  /**
   * Anticipos: la suma, cuántos hay y lo que queda por cobrar.
   *
   * Vienen calculados por el servidor y no se recalculan aquí. El total de la
   * cotización **no** baja con los anticipos: es el número con el que se factura.
   * Lo que baja es `balance`, que es lo que queda por cobrar.
   */
  advances_total: string
  advances_count: number
  has_advances: boolean
  balance: string
  /** Historial. Solo viene en la ficha; en las listas llega vacío. */
  advances: QuoteAdvance[]
  created_at: string | null
  updated_at: string | null
}

/** Ítem tal y como lo envía el cotizador, antes de que el backend lo normalice. */
export interface QuoteItemInput {
  product_id?: number | null
  set_id?: number | null
  custom_name?: string | null
  reference?: string | null
  name?: string
  price: number
  quantity: number
  children?: QuoteItemInput[]
  // Precios del catálogo, para poder cambiar el tipo de facturación sin
  // recargar el catálogo. Solo los ítems que vienen del catálogo los traen;
  // los personalizados y los cargados desde una cotización guardada no.
  retail_price?: number
  wholesale_price?: number
}

/**
 * Una línea del cotizador: lo que se manda al servidor más su clave local.
 *
 * La clave local **no** es el índice en la lista. El índice cambia en cuanto se quita
 * un artículo, y con el índice el acordeón abierto y su buscador se quedarían pegados
 * al set de al lado.
 *
 * `key` no viaja: `payloadActual()` la quita antes de enviar, porque el backend no la
 * conoce y mandarla sería mandar un campo que no está en su contrato.
 */
export interface QuoteLine extends QuoteItemInput {
  key: string
}

export interface QuoteHeaderInput {
  customer_id?: number | null
  customer_name: string
  document?: string | null
  /** Opcionales: se rellenan si se conocen. */
  customer_email?: string | null
  customer_phone?: string | null
  /**
   * Ciudad y origen del cliente. Igual que el correo y el teléfono: copia local para que el
   * directorio no los pierda, y porque el cotizador es el primer sitio donde se teclean.
   */
  customer_city?: string | null
  customer_source?: string | null
  attention_line?: string | null
  quote_date?: string | null
  /**
   * Obligatorio. Antes era opcional y una edición con `null` dejaba la cotización sin
   * tipo, con lo que no se sabía si sus precios eran de la columna minorista o de la
   * mayorista.
   */
  price_type: PriceType
  status?: QuoteStatus
  user_id?: number | null
  is_invoiced?: boolean
  /** Filtra por si la cotización tiene anticipos. */
  has_advances?: boolean
  invoice_number?: string | null
  next_follow_up_date?: string | null
}

export interface QuotePayload extends QuoteHeaderInput {
  items: QuoteItemInput[]
}

export interface QuoteMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  query: Record<string, unknown>
}

export interface QuotePage {
  data: Quote[]
  meta: QuoteMeta
}

export interface Advisor {
  id: number
  name: string
}

export type FollowUpFilter = 'vencido' | 'hoy' | 'proximo' | 'sin_seguimiento' | 'con_notas_nuevas'

export interface QuoteListParams {
  search?: string
  date_from?: string
  date_to?: string
  price_type?: PriceType
  status?: QuoteStatus
  user_id?: number
  is_invoiced?: boolean
  /** Filtra por si la cotización tiene anticipos. */
  has_advances?: boolean
  follow_up_status?: FollowUpFilter
  page?: number
  per_page?: number
}

export interface NotePayload {
  note: string
  type?: NoteType
  next_follow_up_date?: string | null
}

export interface StatusPayload {
  status: QuoteStatus
  is_invoiced?: boolean
  invoice_number?: string | null
}

/** Estados logísticos, en el orden en que se recorren. */
export const QUOTE_STATUSES: QuoteStatus[] = ['borrador', 'pendiente', 'separada', 'empacada', 'enviada', 'cancelada']

export const NOTE_TYPES: NoteType[] = ['general', 'llamada', 'whatsapp', 'visita']

/**
 * Descarta los filtros vacíos antes de la petición.
 *
 * Un `status: ''` llegaría al backend como valor presente y no válido, y
 * respondería 422: el usuario vería un error en un filtro que cree no haber
 * aplicado.
 */
function withoutEmpty<T extends object>(params: T): Partial<T> {
  const cleaned: Partial<T> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    cleaned[key as keyof T] = value as T[keyof T]
  }
  return cleaned
}

/**
 * Normaliza los parámetros del listado antes de mandarlos.
 *
 * Los booleanos se convierten a `1`/`0` a propósito. Axios serializa un
 * booleano de JavaScript como la cadena `"true"` en la query, y la regla
 * `boolean` de Laravel solo acepta `true, false, 0, 1, "0", "1"`: la cadena
 * `"true"` no está en la lista y el backend respondería 422 a un filtro que en
 * pantalla parece perfectamente válido.
 */
function listParams(params: QuoteListParams): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    cleaned[key] = typeof value === 'boolean' ? (value ? 1 : 0) : value
  }

  return cleaned
}

export const quoteApi = {
  list(params: QuoteListParams = {}) {
    return api.get<QuotePage>('/quotes', { params: listParams(params) })
  },

  /**
   * Abre un borrador en blanco.
   *
   * Se pide al servidor en vez de crear el formulario vacío en el cliente, para
   * que exista un `quote_id` al que auto-guardar desde el primer segundo: si no,
   * un corte de luz antes del primer guardado dejaría la cotización perdida.
   */
  createDraft() {
    return api.post<ApiResponse<Quote>>('/quotes/create')
  },

  get(id: number) {
    return api.get<ApiResponse<Quote>>(`/quotes/${id}`)
  },

  save(payload: QuotePayload) {
    return api.post<ApiResponse<Quote>>('/quotes', payload)
  },

  update(id: number, payload: QuotePayload) {
    return api.patch<ApiResponse<Quote>>(`/quotes/${id}`, payload)
  },

  updateStatus(id: number, payload: StatusPayload) {
    return api.patch<ApiResponse<Quote>>(`/quotes/${id}/status`, payload)
  },

  remove(id: number) {
    return api.delete(`/quotes/${id}`)
  },

  addNote(id: number, payload: NotePayload) {
    return api.post<{ note: QuoteNote; unread_notes_count: number; next_follow_up_date: string | null; last_follow_up_note: string | null }>(`/quotes/${id}/notes`, payload)
  },

  /**
   * Anticipos de una cotización, con el total y el saldo recalculados.
   *
   * El getter existe aunque la ficha ya los traiga en su recurso: añadir o borrar
   * uno devuelve las cifras nuevas, y así no hay que recargar la cotización entera
   * para que el saldo de arriba se entere.
   */
  listAdvances(id: number) {
    return api.get<ApiResponse<QuoteAdvance[]>>(`/quotes/${id}/advances`)
  },

  /** Registra un anticipo. Devuelve el saldo recalculado. */
  createAdvance(id: number, payload: QuoteAdvanceInput) {
    return api.post<ApiResponse<AdvanceTotals & { advance: QuoteAdvance }>>(
      `/quotes/${id}/advances`,
      payload,
    )
  },

  /** Borra un anticipo. Devuelve el saldo recalculado. */
  deleteAdvance(id: number, advanceId: number) {
    return api.delete<ApiResponse<AdvanceTotals>>(`/quotes/${id}/advances/${advanceId}`)
  },

  /**
   * Auto-guardado del cotizador.
   *
   * Devuelve el `quote_id` porque en el primer guardado aún no lo hay: el
   * cliente necesita saber cuál acaba de crearse para seguir guardando sobre él.
   */
  /**
   * Autoguardado del cotizador.
   *
   * El tipo va envuelto en `ApiResponse` porque el endpoint sí devuelve el sobre
   * `{data, meta}` del proyecto. Estaba declarado como el contenido a secas, así que
   * leer `quote_id` de la respuesta daba `undefined` con el servidor delante y
   * TypeScript no lo señalaba.
   */
  autoSave(payload: QuotePayload & { quote_id?: number | null }) {
    return api.post<ApiResponse<{ quote_id: number; status: QuoteStatus; total_price: string }>>('/quotes/auto-save', withoutEmpty(payload))
  },

  advisors() {
    return api.get<ApiResponse<Advisor[]>>('/quotes/advisors')
  },
}

export type { CustomerSuggestion }
